import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, CheckCircle, Clock, Send, Copy, ExternalLink, Trash2, Mail, AlertCircle, Loader2, Building2, Building, Download, FileText, Sparkles, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BLOCK_SCOPE_LABELS = {
  stairwells_landings: 'Stairwells & landings',
  communal_corridors: 'Communal corridors',
  entrances_lobbies: 'Entrances & lobbies',
  lifts: 'Lifts',
  bin_areas: 'Bin areas',
  cobweb_high_level_dusting: 'Cobweb & high-level dusting',
  glass_internal_doors: 'Glass & internal entrance doors',
  external_litter_grounds: 'External litter picking / grounds',
};

const OFFICE_SCOPE_LABELS = {
  general_office_desks_floors: 'General office cleaning (desks, floors, bins)',
  kitchen_breakroom: 'Kitchen & breakroom',
  bathrooms_wc: 'Bathrooms & WC',
  meeting_rooms: 'Meeting & conference rooms',
  reception_area: 'Reception area',
  window_cleaning_internal: 'Window cleaning (internal)',
  window_cleaning_external: 'Window cleaning (external)',
  carpet_cleaning: 'Carpet cleaning',
  deep_cleaning: 'Deep cleaning',
  waste_recycling: 'Waste & recycling management',
  sanitisation_disinfection: 'Sanitisation & disinfection',
};

const WINDOW_SCOPE_LABELS = {
  external_windows: 'External windows',
  internal_windows: 'Internal windows',
  frames_sills: 'Window frames & sills',
  conservatory_roof: 'Conservatory / glass roof',
  skylights: 'Skylights & roof lights',
  glass_doors: 'Glass doors & panels',
  fascia_soffits: 'Fascia & soffits',
  gutters: 'Gutter cleaning',
  cladding: 'Cladding cleaning',
  pressure_washing: 'Pressure washing (paths/patios)',
};

const FLOOR_AREA_LABELS = {
  staircases: 'Staircases',
  landings: 'Landings',
  entrances: 'Entrances',
  corridors: 'Corridors',
  bin_store: 'Bin Store',
};

const FLOOR_SERVICE_LABELS = {
  deep_clean: 'Deep Clean Only',
  strip_polish: 'Strip & Polish (Standard)',
  pu_coating: 'Full Protection System (PU Coating)',
};

const CONTRACT_LABELS = {
  rolling_monthly: 'Rolling monthly',
  '3_month': '3-month minimum',
  '6_month': '6-month minimum',
};

const FREQUENCY_LABELS = {
  daily: 'Daily',
  every_other_day: 'Every other day',
  twice_weekly: 'Twice weekly',
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  every_6_weeks: 'Every 6 weeks',
  every_8_weeks: 'Every 8 weeks',
  quarterly: 'Quarterly',
  one_off: 'One-off clean',
};

const QuoteDetail = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const BASE_URL = window.location.origin;
  
  const [quote, setQuote] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Check if email has already been sent by looking at events
  const hasEmailBeenSent = events.some(e => e.event_type === 'email_sent');

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}`);
      if (response.ok) {
        const data = await response.json();
        setQuote(data.quote);
        setEvents(data.events || []);
      } else {
        navigate('/admin');
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const sendQuoteEmail = async () => {
    setSendingEmail(true);
    setEmailError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        // Refresh quote to get updated events (will show email_sent)
        fetchQuote();
      } else {
        const data = await response.json();
        setEmailError(data.detail || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError('Connection error. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const copyQuoteLink = async () => {
    const link = `${BASE_URL}/quote/${quoteId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        navigate('/admin');
      }
    } catch (err) {
      console.error('Error deleting quote:', err);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'sent':
        return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Sent' };
      case 'viewed':
        return { icon: Eye, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Viewed' };
      case 'accepted':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Accepted' };
      case 'expired':
        return { icon: AlertCircle, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Expired' };
      default:
        return { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100', label: status };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const getSelectedScope = () => {
    const isOffice = quote?.quote_type === 'office';
    const isWindow = quote?.quote_type === 'window';
    const isFloor = quote?.quote_type === 'floor';
    
    if (isFloor) {
      const areas = quote?.floor_details?.areas_included || {};
      return Object.entries(areas)
        .filter(([_, selected]) => selected)
        .map(([key, _]) => FLOOR_AREA_LABELS[key] || key);
    }
    
    const scopeData = isWindow ? quote?.window_scope_of_works : isOffice ? quote?.office_scope_of_works : quote?.scope_of_works;
    const scopeLabels = isWindow ? WINDOW_SCOPE_LABELS : isOffice ? OFFICE_SCOPE_LABELS : BLOCK_SCOPE_LABELS;
    
    if (!scopeData) return [];
    return Object.entries(scopeData)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => scopeLabels[key] || key);
  };

  const getFormSubmitMailtoLink = () => {
    if (!quote) return '';
    
    const siteAddress = quote.site_address || quote.site_location || '';
    const isOffice = quote.quote_type === 'office';
    const isWindow = quote.quote_type === 'window';
    const isFloor = quote.quote_type === 'floor';
    const serviceType = isFloor ? 'Floor Restoration' : isWindow ? 'Window Cleaning' : isOffice ? 'Office Cleaning' : 'Communal Cleaning';
    const subject = `${serviceType} Quote – ${siteAddress}`;
    const quoteLink = `${BASE_URL}/quote/${quote.id}`;
    
    let body;
    if (isFloor) {
      const fd = quote.floor_details || {};
      const subCost = fd.subcontractor_cost || 0;
      const addCost = fd.additional_costs || 0;
      const totalCost = subCost + addCost;
      const stripPrice = Math.ceil(totalCost * 1.275);
      const puPrice = Math.ceil(totalCost * 1.35);
      
      body = `Dear ${quote.contact_name},

Thank you for your interest in our floor restoration services.

Please find your quote for ${siteAddress} below:

Option 1 – Strip & Polish: ${formatPrice(stripPrice)}
Option 2 – PU Coating (Recommended): ${formatPrice(puPrice)}

View your full quote here:
${quoteLink}

This quote is valid for 30 days.

Best regards,
Just Cleaned`;
    } else {
      const areaType = isWindow ? 'external window cleaning' : isOffice ? 'office cleaning' : 'communal block cleaning';
      const priceText = isWindow && quote.pricing_type === 'per_clean' 
        ? `Price Per Clean: ${formatPrice(quote.per_clean_price)}`
        : `Monthly Price: ${formatPrice(quote.monthly_price)}`;
      
      body = `Dear ${quote.contact_name},

Thank you for your interest in our ${areaType} services.

Please find your quote for ${siteAddress} below:

${priceText} ${quote.vat_registered ? '+ VAT' : '(VAT not applicable)'}
Frequency: ${FREQUENCY_LABELS[quote.cleaning_frequency] || quote.cleaning_frequency} cleaning

View and accept your quote here:
${quoteLink}

This quote is valid for 30 days.

You can accept by clicking the button in the link above, or simply reply to this email with "Approved".

Best regards,
Just Cleaned`;
    }

    return `mailto:${quote.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1f4e]"></div>
      </div>
    );
  }

  if (!quote) return null;

  const statusConfig = getStatusConfig(quote.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">{quote.company_name}</h1>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                {statusConfig.label}
              </span>
            </div>
            <p className="text-gray-500 text-sm mt-1">Quote {quote.id}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={copyQuoteLink}
            className="h-10 px-4 rounded-lg"
          >
            {copying ? <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> : <Copy className="w-4 h-4 mr-2" />}
            {copying ? 'Copied!' : 'Copy Link'}
          </Button>
          
          {/* Download PDFs Dropdown */}
          <div className="relative group">
            <Button
              variant="outline"
              className="h-10 px-4 rounded-lg"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDFs
            </Button>
            <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <a
                href={`${API_URL}/api/admin/quotes/${quote.id}/download-all-pdfs?doc_type=quote`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FileText className="w-4 h-4" />
                Quote PDF
              </a>
              <a
                href={`${API_URL}/api/admin/quotes/${quote.id}/download-all-pdfs?doc_type=schedule`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FileText className="w-4 h-4" />
                Cleaning Schedule
              </a>
              <a
                href={`${API_URL}/api/admin/quotes/${quote.id}/download-all-pdfs?doc_type=method`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FileText className="w-4 h-4" />
                Method Statement
              </a>
              <a
                href={`${API_URL}/api/admin/quotes/${quote.id}/download-all-pdfs?doc_type=risk`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FileText className="w-4 h-4" />
                Risk Assessment
              </a>
              <a
                href={`${API_URL}/api/admin/quotes/${quote.id}/download-all-pdfs?doc_type=terms`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FileText className="w-4 h-4" />
                Terms & Conditions
              </a>
            </div>
          </div>
          
          <a
            href={`/quote/${quote.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Preview
          </a>
          
          {/* Email Button - Show different states based on whether email was sent */}
          {hasEmailBeenSent && !sendingEmail ? (
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-2 h-10 px-4 rounded-lg bg-green-600 text-white text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Email Sent
              </div>
              <button
                onClick={sendQuoteEmail}
                className="mt-1 text-xs text-gray-500 hover:text-[#1a1f4e] underline"
              >
                Resend to {quote.email}
              </button>
            </div>
          ) : (
            <Button
              onClick={sendQuoteEmail}
              disabled={sendingEmail}
              className="h-10 px-4 rounded-lg bg-[#1a1f4e] hover:bg-[#2a2f5e] text-white text-sm font-medium transition-colors"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send to {quote.email}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Email Error Message */}
      {emailError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {emailError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Details */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-medium text-gray-900">{quote.company_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Contact</p>
                <p className="font-medium text-gray-900">{quote.contact_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{quote.email}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-500">Site Address</p>
                <p className="font-medium text-gray-900">{quote.site_address || quote.site_location}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{quote.quote_type === 'floor' ? 'Blocks' : quote.quote_type === 'window' ? 'Property Details' : quote.quote_type === 'office' ? 'Office Size / Floors' : 'Blocks / Entrances'}</p>
                <p className="font-medium text-gray-900">{quote.number_of_blocks}</p>
              </div>
            </div>
          </section>

          {/* Service Details */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h2>
            <div className="mb-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                quote.quote_type === 'floor' ? 'bg-amber-100 text-amber-600' :
                quote.quote_type === 'window' ? 'bg-green-100 text-green-600' :
                quote.quote_type === 'office' ? 'bg-blue-100 text-blue-600' : 
                'bg-purple-100 text-purple-600'
              }`}>
                {quote.quote_type === 'floor' ? <Layers className="w-5 h-5" /> :
                 quote.quote_type === 'window' ? <Sparkles className="w-5 h-5" /> :
                 quote.quote_type === 'office' ? <Building className="w-5 h-5" /> : 
                 <Building2 className="w-5 h-5" />}
              </div>
              <div>
                <p className="text-sm text-gray-500">Quote Type</p>
                <p className="font-medium text-gray-900 capitalize">
                  {quote.quote_type === 'floor' ? 'Floor Restoration' :
                   quote.quote_type === 'window' ? 'External Window Cleaning' :
                   quote.quote_type === 'office' ? 'Office Cleaning' : 
                   'Communal Block Cleaning'}
                </p>
              </div>
            </div>
            
            {/* Floor-specific details */}
            {quote.quote_type === 'floor' && quote.floor_details && (
              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <p className="text-sm text-gray-500">Floor Type</p>
                    <p className="font-medium text-gray-900">{(quote.floor_details.floor_type || '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Condition</p>
                    <p className="font-medium text-gray-900">{(quote.floor_details.condition || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Service Type</p>
                    <p className="font-medium text-gray-900">{FLOOR_SERVICE_LABELS[quote.floor_details.service_type] || quote.floor_details.service_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Floor Area</p>
                    <p className="font-medium text-gray-900">{quote.floor_details.total_floor_area} m²</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Blocks</p>
                    <p className="font-medium text-gray-900">{quote.floor_details.num_blocks}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Floors per Block</p>
                    <p className="font-medium text-gray-900">{quote.floor_details.floors_per_block}</p>
                  </div>
                </div>
                {quote.floor_details.access_notes && (
                  <div>
                    <p className="text-sm text-gray-500">Access Notes</p>
                    <p className="text-gray-700">{quote.floor_details.access_notes}</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">Cleaning Frequency</p>
              <p className="font-medium text-gray-900">{FREQUENCY_LABELS[quote.cleaning_frequency] || quote.cleaning_frequency}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-2">{quote.quote_type === 'floor' ? 'Areas Included' : 'Scope of Works'}</p>
              <div className="flex flex-wrap gap-2">
                {getSelectedScope().map((item, index) => (
                  <span key={index} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing & Terms */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Terms</h2>
            
            {quote.quote_type === 'floor' && quote.floor_details ? (
              /* Floor Restoration Pricing - Two Options */
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Option 1 – Strip & Polish</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(Math.ceil(((quote.floor_details.subcontractor_cost || 0) + (quote.floor_details.additional_costs || 0)) * 1.275))}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Annual maintenance required</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Option 2 – PU Coating</p>
                      <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Recommended</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(Math.ceil(((quote.floor_details.subcontractor_cost || 0) + (quote.floor_details.additional_costs || 0)) * 1.35))}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">3-5 year protection</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-sm text-gray-500">Subcontractor Cost</p>
                    <p className="font-medium text-gray-900">{formatPrice(quote.floor_details.subcontractor_cost || 0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Additional Costs</p>
                    <p className="font-medium text-gray-900">{formatPrice(quote.floor_details.additional_costs || 0)}</p>
                  </div>
                  {quote.floor_details.labour_days > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Labour Days</p>
                      <p className="font-medium text-gray-900">{quote.floor_details.labour_days}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Standard Pricing */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-[#00c853]/10 rounded-xl p-4">
                  {quote.quote_type === 'window' && quote.pricing_type === 'per_clean' ? (
                    <>
                      <p className="text-sm text-gray-600">Price Per Clean</p>
                      <p className="text-2xl font-bold text-gray-900">{formatPrice(quote.per_clean_price)}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">Monthly Price</p>
                      <p className="text-2xl font-bold text-gray-900">{formatPrice(quote.monthly_price)}</p>
                    </>
                  )}
                  <p className="text-xs text-gray-500">{quote.vat_registered ? '+ VAT' : 'VAT not applicable'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Contract Type</p>
                  <p className="font-medium text-gray-900">{CONTRACT_LABELS[quote.contract_type] || quote.contract_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Notice Period</p>
                  <p className="font-medium text-gray-900">{quote.notice_period} days</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Start Timeframe</p>
                  <p className="font-medium text-gray-900">{quote.start_timeframe}</p>
                </div>
              </div>
            )}
            
            {quote.notes && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="text-gray-700">{quote.notes}</p>
              </div>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quote Stats */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quote Activity</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium text-gray-900">{formatDate(quote.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expires</p>
                <p className="font-medium text-gray-900">{formatDate(quote.expires_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Views</p>
                <p className="font-medium text-gray-900">{quote.view_count || 0}</p>
              </div>
              {quote.first_viewed_at && (
                <div>
                  <p className="text-sm text-gray-500">First Viewed</p>
                  <p className="font-medium text-gray-900">{formatDate(quote.first_viewed_at)}</p>
                </div>
              )}
              {quote.accepted_at && (
                <div>
                  <p className="text-sm text-gray-500">Accepted</p>
                  <p className="font-medium text-green-600">{formatDate(quote.accepted_at)}</p>
                </div>
              )}
            </div>
          </section>

          {/* Activity Log */}
          {events.length > 0 && (
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Log</h2>
              <div className="space-y-3">
                {events.map((event, index) => (
                  <div key={event.id || index} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      event.event_type === 'accepted' ? 'bg-green-500' :
                      event.event_type === 'viewed' ? 'bg-amber-500' :
                      'bg-blue-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900 capitalize">{event.event_type}</p>
                      <p className="text-xs text-gray-500">{formatDate(event.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Delete Quote */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Danger Zone</h2>
            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full h-10 border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Quote
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Are you sure? This cannot be undone.</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 h-10"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetail;
